/**
 * Rapid-fire pre-flight card. Answers the seven things a respondent could previously only learn by
 * losing seconds off a running clock; every rule line is derived from the block — ADR-0004 §1.
 */
import React from 'react';
import { Zap } from 'lucide-react';
import Button from '../../components/Button';
import type { RapidFireRule } from './rapidFire';

interface RapidFireIntroProps {
  label: string;
  introText: string;
  durationSeconds: number;
  /** Derived from the block's config and contents, never authored. */
  rules: RapidFireRule[];
  onStart: () => void;
  /** True while the Start request is in flight, so the button can't be double-fired. */
  starting?: boolean;
}

const RapidFireIntro: React.FC<RapidFireIntroProps> = ({
  label,
  introText,
  durationSeconds,
  rules,
  onStart,
  starting = false,
}) => (
  <div className="text-center px-8 py-12">
    <div className="w-[72px] h-[72px] rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
      <Zap className="w-8 h-8 text-blue-600" aria-hidden="true" />
    </div>

    <h1 className="text-3xl font-bold text-gray-900 mb-3">{label}</h1>
    <p className="text-lg leading-relaxed text-gray-700 max-w-[44ch] mx-auto mb-7">{introText}</p>

    <div className="mb-7">
      <span className="block text-5xl font-bold text-gray-900 tabular-nums leading-none">
        {durationSeconds}
      </span>
      <span className="block text-xs font-medium uppercase tracking-widest text-gray-500 mt-2">
        seconds on the clock
      </span>
    </div>

    <ul className="text-left max-w-[44ch] mx-auto mb-8 divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
      {rules.map((rule) => (
        <li
          key={rule.id}
          className={`px-4 py-3 text-sm leading-snug ${
            rule.emphasis === 'rule' ? 'text-gray-900' : 'text-gray-600'
          }`}
        >
          {rule.text}
        </li>
      ))}
    </ul>

    <Button variant="primary" onClick={onStart} disabled={starting}>
      {starting ? 'Starting…' : `I’m ready — start the ${durationSeconds} seconds`}
    </Button>
    <p className="text-xs text-gray-500 mt-3">
      You’ll get a 3‑2‑1 countdown first. The clock starts after it, not now.
    </p>
  </div>
);

export default RapidFireIntro;
